/**
 * Help questions (`/help-log`), alleen voor de owner (18-10-2026, Jordy).
 *
 * Elke vraag aan de Help-assistent met het antwoord en WIE hem stelde (niet
 * anoniem, Jordy 18-10). "Unanswered" = de assistent wist het niet of
 * weigerde: dat is waar de handleiding (src/content/chatter-guide/guide.en.md) iets
 * mist. Endpoint: GET /api/admin/help/log.
 */
import { useState } from 'react'
import { ErrorNote, Loading, Panel, Table, Tag } from '../components/ui'
import { useApi } from '../lib/useApi'
import { dt } from '../lib/format'

interface Rij {
  id: number
  created_at: string | null
  user_id: number | null
  user: string
  username: string | null
  role: string | null
  question: string
  answer: string
  answered: boolean
  model: string
  cost_eur: number
}
interface Log {
  total: number
  cost_today_eur: number
  daily_budget_eur: number
  users: { id: number; name: string }[]
  data: Rij[]
}

const PER_PAGINA = 100

export default function HelpLog() {
  const [alleenOnbeantwoord, setAlleenOnbeantwoord] = useState(false)
  const [gebruiker, setGebruiker] = useState('')
  const [pagina, setPagina] = useState(0)
  const pad = `/api/admin/help/log?limit=${PER_PAGINA}&offset=${pagina * PER_PAGINA}` +
    (alleenOnbeantwoord ? '&unanswered=true' : '') + (gebruiker ? `&user_id=${gebruiker}` : '')
  const log = useApi<Log>(pad, [pad])
  const d = log.data

  return (
    <Panel
      title="Help questions"
      desc={'Every question chatters asked the Help assistant, with who asked it. ' +
        '"Unanswered" means the guide did not cover it: add it to the Chatter Startup Guide.'}
      right={
        <div className="row" style={{ gap: 8 }}>
          <label className="row" style={{ gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={alleenOnbeantwoord}
                   onChange={(e) => { setAlleenOnbeantwoord(e.target.checked); setPagina(0) }} />
            Unanswered only
          </label>
          <select className="select" aria-label="Filter by user" value={gebruiker}
                  onChange={(e) => { setGebruiker(e.target.value); setPagina(0) }}>
            <option value="">Everyone</option>
            {(d?.users ?? []).map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
          </select>
        </div>
      }
      flush
    >
      {log.loading && !d ? <Loading /> : null}
      {log.error ? <ErrorNote error={log.error} /> : null}
      {d ? (
        <>
          <div className="mut2" style={{ fontSize: 12, padding: '8px 16px' }}>
            {d.total} question(s) · cost today €{d.cost_today_eur.toFixed(2)} of €{d.daily_budget_eur.toFixed(2)} daily limit
          </div>
          <Table emptyText="No questions yet." cols={[
            { key: 'tijd', label: 'Time' },
            { key: 'wie', label: 'Asked by' },
            { key: 'vraag', label: 'Question' },
            { key: 'antwoord', label: 'Answer' },
            { key: 'stand', label: 'Status' },
          ]}>
            {d.data.map((r) => (
              <tr key={r.id}>
                <td style={{ whiteSpace: 'nowrap' }}>{dt(r.created_at)}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <b>{r.user}</b>
                  {r.username && r.username !== r.user ? <div className="mut2" style={{ fontSize: 11 }}>{r.username}</div> : null}
                  {r.role ? <div className="mut2" style={{ fontSize: 11 }}>{r.role}</div> : null}
                </td>
                <td style={{ maxWidth: 320, whiteSpace: 'pre-wrap' }}>{r.question}</td>
                <td style={{ maxWidth: 460, whiteSpace: 'pre-wrap' }} className="mut">{r.answer}</td>
                <td>{r.answered ? <Tag kind="ok">Answered</Tag> : <Tag kind="warn">Unanswered</Tag>}</td>
              </tr>
            ))}
          </Table>
          {d.total > PER_PAGINA ? (
            <div className="row" style={{ gap: 8, padding: 12, justifyContent: 'flex-end' }}>
              <button className="btn ghost sm" type="button" disabled={pagina === 0}
                      onClick={() => setPagina((p) => p - 1)}>← Previous</button>
              <button className="btn ghost sm" type="button" disabled={(pagina + 1) * PER_PAGINA >= d.total}
                      onClick={() => setPagina((p) => p + 1)}>Next →</button>
            </div>
          ) : null}
        </>
      ) : null}
    </Panel>
  )
}
